import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { MainLayout } from "@/components/layout/MainLayout";
import { HeroSection } from "@/components/home/HeroSection";
import { GeneratorLinks } from "@/components/home/GeneratorLinks";
import { BulkUrlInput } from "@/components/BulkUrlInput";
import { EmptyState } from "@/components/EmptyState";

// Lazy-load below-fold and conditional components
const CrawlInput = lazy(() => import("@/components/CrawlInput").then(m => ({ default: m.CrawlInput })));
const PageAnalysis = lazy(() => import("@/components/PageAnalysis").then(m => ({ default: m.PageAnalysis })));
const SchemaCard = lazy(() => import("@/components/SchemaCard").then(m => ({ default: m.SchemaCard })));
const ExportAllSchemas = lazy(() => import("@/components/ExportAllSchemas").then(m => ({ default: m.ExportAllSchemas })));
const FAQ = lazy(() => import("@/components/FAQ").then(m => ({ default: m.FAQ })));
const SEOBenefits = lazy(() => import("@/components/home/SEOBenefits").then(m => ({ default: m.SEOBenefits })));
const MonitoringHighlight = lazy(() => import("@/components/home/MonitoringHighlight").then(m => ({ default: m.MonitoringHighlight })));

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useSubscription, getLocalizedPlans } from "@/hooks/useSubscription";
import { useAnalysisHistory } from "@/hooks/useAnalysisHistory";
import { useRestoredResults } from "@/hooks/useRestoredResults";
import { useLocalePath } from "@/hooks/useLocalePath";
import { useDailyFreeUsage } from "@/hooks/useDailyFreeUsage";
import { analyzePageUrl, analyzePageUrls, analyzePageUrlFree, type AnalysisResult } from "@/lib/api/schema-analyzer";
import { QuotaReachedDialog } from "@/components/QuotaReachedDialog";
import { FreeQuotaBanner } from "@/components/FreeQuotaBanner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, List, Globe, ChevronDown, Package, Sparkles, ArrowRight, History } from "lucide-react";

const FREE_TRIAL_KEY = 'schemasgen_free_trial_uses';
const FREE_TRIAL_DAILY_LIMIT = 3;
const FREE_WELCOME_BULK_LIMIT = 5;

const Index = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | undefined>();
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { credits, hasCredits, refetch: refetchCredits } = useCredits();
  const { currentPlan } = useSubscription();
  const { addToHistory } = useAnalysisHistory();
  const { isQuotaReached, remaining: freeRemaining, isFreeUser, refetch: refetchDailyUsage } = useDailyFreeUsage();
  const navigate = useNavigate();
  const lp = useLocalePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasNewScan, setHasNewScan] = useState(false);
  const { restoredResults, isRestored } = useRestoredResults();
  const hasRestoredRef = useRef(false);
  
  // i18n helper for inline texts
  const tx = (fr: string, en: string, es: string) =>
    language === 'fr' ? fr : language === 'es' ? es : en;

  // Support ?tab=crawl from "New Project" button in account
  const initialTab = searchParams.get('tab') === 'crawl' ? 'crawl' : 'urls';
  const [activeInputTab, setActiveInputTab] = useState(initialTab);
  
  const handleInputTabChange = (value: string) => {
    setActiveInputTab(value);
    if (searchParams.has('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Restore last results from history on mount (only if no new scan started)
  useEffect(() => {
    if (!hasNewScan && isRestored && restoredResults.length > 0 && !hasRestoredRef.current) {
      hasRestoredRef.current = true;
      setResults(restoredResults);
    }
  }, [isRestored, restoredResults, hasNewScan]);

  // Get bulk limit from current plan
  // Free accounts keep welcome credits (never expire) for multi-page analyses and site exploration
  const welcomeCredits = isFreeUser ? (credits?.credits_balance || 0) : 0;
  const bulkLimit = isFreeUser && welcomeCredits > 0
    ? FREE_WELCOME_BULK_LIMIT
    : (currentPlan?.bulkLimit || getLocalizedPlans('en')[0].bulkLimit);
  // Free users: single URL uses the daily quota, several URLs use welcome credits
  const creditsAvailable = isFreeUser
    ? Math.max(freeRemaining, welcomeCredits)
    : (credits?.credits_balance || 0);

  // Anonymous visitors get FREE_TRIAL_DAILY_LIMIT analyses per rolling 24h
  const getFreeTrialUses = (): number[] => {
    try {
      const parsed = JSON.parse(localStorage.getItem(FREE_TRIAL_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((t) => typeof t === 'number' && Date.now() - t < 24 * 60 * 60 * 1000);
    } catch {
      return [];
    }
  };

  const hasFreeTrialUsed = () => getFreeTrialUses().length >= FREE_TRIAL_DAILY_LIMIT;

  const markFreeTrialUsed = () => {
    localStorage.setItem(FREE_TRIAL_KEY, JSON.stringify([...getFreeTrialUses(), Date.now()]));
  };

  const markFreeTrialExhausted = () => {
    const now = Date.now();
    localStorage.setItem(FREE_TRIAL_KEY, JSON.stringify(Array(FREE_TRIAL_DAILY_LIMIT).fill(now)));
  };

  const handleScan = async (urls: string[], projectId?: string) => {
    // SAFETY: Enforce bulk limit on client side before sending to server
    const effectiveLimit = Math.min(creditsAvailable || 0, bulkLimit || 5);
    let urlsToProcess = urls;
    
    if (isAuthenticated && urls.length > effectiveLimit && effectiveLimit > 0) {
      console.warn(`handleScan: trimming URLs from ${urls.length} to ${effectiveLimit}`);
      urlsToProcess = urls.slice(0, effectiveLimit);
      toast({
        title: tx("URLs limitées", "URLs limited", "URLs limitadas"),
        description: tx(
          `Analyse limitée à ${effectiveLimit} URLs (votre limite actuelle).`,
          `Analysis limited to ${effectiveLimit} URLs (your current limit).`,
          `Análisis limitado a ${effectiveLimit} URLs (tu límite actual).`
        ),
      });
    }

    // If not authenticated, allow FREE_TRIAL_DAILY_LIMIT free analyses per day
    if (!isAuthenticated) {
      if (urlsToProcess.length > 1) {
        toast({
          title: tx("Compte requis", "Account required", "Cuenta requerida"),
          description: tx(
            "Créez un compte gratuit : 5 crédits offerts pour analyser plusieurs pages d'un coup.",
            "Create a free account: 5 bonus credits to analyze several pages at once.",
            "Crea una cuenta gratuita: 5 créditos de regalo para analizar varias páginas a la vez."
          ),
          variant: "destructive",
        });
        return;
      }

      if (hasFreeTrialUsed()) {
        // Don't show a toast - the FreeQuotaBanner will handle the UX
        return;
      }

      // Process free trial
      setHasNewScan(true);
      setIsLoading(true);
      setResults([]);

      try {
        const response = await analyzePageUrlFree(urlsToProcess[0]);
        
        if (response.success && response.data) {
          setResults([response.data]);
          markFreeTrialUsed();
          toast({
            title: t.results.analysisComplete,
            description: `${response.data.generatedSchemas.length} ${t.results.schemasGenerated}`,
          });
        } else {
          if (response.error?.toLowerCase().includes('free trial already used')) {
            markFreeTrialExhausted();
            // No destructive toast - the banner handles it
          } else {
            toast({
              title: t.errors.title,
              description: response.error || t.errors.analysisFailed,
              variant: "destructive",
            });
          }
        }
      } catch (error) {
        console.error('Free trial analysis error:', error);
        toast({
          title: t.errors.title,
          description: t.errors.analysisFailed,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Free plan: check daily quota
    const canUseDaily = urlsToProcess.length === 1 && freeRemaining > 0;
    const canUseWelcome = welcomeCredits >= urlsToProcess.length;
    if (isFreeUser && !canUseDaily && !canUseWelcome && isQuotaReached && welcomeCredits === 0) {
      setShowQuotaDialog(true);
      return;
    }

    // Free plan: neither the daily quota nor welcome credits cover the request
    if (isFreeUser && !canUseDaily && !canUseWelcome) {
      toast({
        title: tx("Quota insuffisant", "Insufficient quota", "Cuota insuficiente"),
        description: tx(
          `Il vous reste ${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} aujourd'hui.`,
          `You have ${freeRemaining} analysis${freeRemaining > 1 ? 'es' : ''} left today.`,
          `Te quedan ${freeRemaining} análisis hoy.`
        ),
      });
      return;
    }

    // Paid users: check credits
    if (!isFreeUser && !hasCredits) {
      setShowQuotaDialog(true);
      return;
    }

    // Check if paid user has enough credits for the request
    if (!isFreeUser && credits && credits.credits_balance < urlsToProcess.length) {
      toast({
        title: tx("Crédits insuffisants", "Insufficient credits", "Créditos insuficientes"),
        description: tx(
          `Vous avez ${credits.credits_balance} crédit(s) mais vous essayez d'analyser ${urlsToProcess.length} URL(s).`,
          `You have ${credits.credits_balance} credit(s) but are trying to analyze ${urlsToProcess.length} URL(s).`,
          `Tienes ${credits.credits_balance} crédito(s) pero intentas analizar ${urlsToProcess.length} URL(s).`
        ),
      });
      return;
    }

    setHasNewScan(true);
    setIsLoading(true);
    setResults([]);

    try {
      if (urlsToProcess.length === 1) {
        const response = await analyzePageUrl(urlsToProcess[0]);
        
        if (response.success && response.data) {
          setResults([response.data]);
          toast({
            title: t.results.analysisComplete,
            description: `${response.data.generatedSchemas.length} ${t.results.schemasGenerated}`,
          });

          await addToHistory({
            url: response.data.url,
            page_type: response.data.pageType,
            title: response.data.title,
            schemas_count: response.data.generatedSchemas.length,
            schemas_data: response.data.generatedSchemas,
            project_id: projectId,
          });
        } else {
          if (response.error?.includes('daily_quota')) {
            setShowQuotaDialog(true);
          } else {
            toast({
              title: t.errors.title,
              description: response.error || t.errors.analysisFailed,
              variant: "destructive",
            });
          }
        }
      } else {
        const response = await analyzePageUrls(urlsToProcess);
        
        if (response.success && response.results) {
          setResults(response.results);
          
          const successCount = response.results.length;
          const errorCount = response.errors?.length || 0;
          
          toast({
            title: t.results.analysisComplete,
            description: tx(
              `${successCount} URL(s) analysée(s)${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}`,
              `${successCount} URL(s) analyzed${errorCount > 0 ? `, ${errorCount} error(s)` : ''}`,
              `${successCount} URL(s) analizada(s)${errorCount > 0 ? `, ${errorCount} error(es)` : ''}`
            ),
          });

          for (const result of response.results) {
            await addToHistory({
              url: result.url,
              page_type: result.pageType,
              title: result.title,
              schemas_count: result.generatedSchemas.length,
              schemas_data: result.generatedSchemas,
              project_id: projectId,
            });
          }
        } else {
          toast({
            title: t.errors.title,
            description: response.errors?.[0]?.error || t.errors.analysisFailed,
            variant: "destructive",
          });
        }
      }

      refetchCredits();
      refetchDailyUsage();
    } catch (error: any) {
      console.error('Analysis error:', error);
      refetchCredits();
      refetchDailyUsage();
      
      // Check if the error is a quota reached error from the backend
      const errorMsg = error?.message || error?.toString() || '';
      if (errorMsg.includes('daily_quota_reached') || errorMsg.includes('daily_quota_exceeded')) {
        setShowQuotaDialog(true);
      } else {
        toast({
          title: t.errors.title,
          description: t.errors.analysisFailed,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show free trial message if not authenticated and not used yet
  const showFreeTrialBanner = !isAuthenticated && !hasFreeTrialUsed();
  // Show upsell after free trial is used
  const showFreeTrialUsedUpsell = !isAuthenticated && hasFreeTrialUsed();
  const showFreeTrialUsedBanner = !isAuthenticated && hasFreeTrialUsed();

  return (
    <MainLayout>
      <div className="relative max-w-7xl mx-auto px-4 py-8">
        <HeroSection />

        <div className="mt-8">
          {/* Free trial banner for non-authenticated users */}
          {showFreeTrialBanner && (
            <Alert className="mb-4 border-primary/20 bg-primary/5">
              <AlertCircle className="h-4 w-4 text-primary" />
              <AlertDescription className="text-primary">
                {tx(
                  "🎉 Testez gratuitement : 3 analyses par jour, sans créer de compte (une page à la fois).",
                  "🎉 Try for free: 3 analyses per day, no account needed (one page at a time).",
                  "🎉 Prueba gratis: 3 análisis por día, sin crear cuenta (una página a la vez)."
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Non-authenticated user who already used free trial */}
          {showFreeTrialUsedBanner && (
            <FreeQuotaBanner variant="signup" />
          )}

          {/* Daily usage info for free users */}
          {isAuthenticated && isFreeUser && (!isQuotaReached || welcomeCredits > 0) && (
            <Alert className="mb-4 border-primary/20 bg-primary/5">
              <AlertCircle className="h-4 w-4 text-primary" />
              <AlertDescription className="text-primary">
                {tx(
                  `${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} gratuite${freeRemaining > 1 ? 's' : ''} restante${freeRemaining > 1 ? 's' : ''} aujourd'hui.`,
                  `${freeRemaining} free analysis${freeRemaining > 1 ? 'es' : ''} remaining today.`,
                  `${freeRemaining} análisis gratuito${freeRemaining > 1 ? 's' : ''} restante${freeRemaining > 1 ? 's' : ''} hoy.`
                )}{" "}
                {welcomeCredits > 0 && tx(
                  `+ ${welcomeCredits} crédit${welcomeCredits > 1 ? 's' : ''} de bienvenue pour analyser plusieurs pages ou explorer un site.`,
                  `+ ${welcomeCredits} welcome credit${welcomeCredits > 1 ? 's' : ''} to analyze several pages or explore a site.`,
                  `+ ${welcomeCredits} crédito${welcomeCredits > 1 ? 's' : ''} de bienvenida para analizar varias páginas o explorar un sitio.`
                )}{" "}
                <Link to="/pricing" className="font-medium hover:underline">
                  {tx("Passer à un forfait", "Upgrade plan", "Mejorar plan")}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {/* Free user quota reached - nice branded card instead of error */}
          {isAuthenticated && isFreeUser && isQuotaReached && welcomeCredits === 0 && (
            <FreeQuotaBanner variant="upgrade" />
          )}

          {/* Warning for low credits (paid users only) */}
          {isAuthenticated && !isFreeUser && credits && credits.credits_balance <= 2 && credits.credits_balance > 0 && (
            <Alert className="mb-4 border-warning/20 bg-warning/5">
              <AlertCircle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning">
                {tx(
                  `Il vous reste ${credits.credits_balance} crédit${credits.credits_balance !== 1 ? "s" : ""}.`,
                  `You have ${credits.credits_balance} credit${credits.credits_balance !== 1 ? "s" : ""} left.`,
                  `Te quedan ${credits.credits_balance} crédito${credits.credits_balance !== 1 ? "s" : ""}.`
                )}{" "}
                <Link to="/pricing" className="font-medium hover:underline">
                  {tx("Passez à un plan supérieur", "Upgrade your plan", "Mejora tu plan")}
                </Link>{" "}
                {tx("pour continuer.", "to continue.", "para continuar.")}
              </AlertDescription>
            </Alert>
          )}

          {/* Warning for no credits (paid users only) */}
          {isAuthenticated && !isFreeUser && credits && credits.credits_balance === 0 && (
            <Alert className="mb-4 border-destructive/20 bg-destructive/5">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                {tx(
                  "Vous n'avez plus de crédits.",
                  "You're out of credits.",
                  "Te has quedado sin créditos."
                )}{" "}
                <Link to="/pricing" className="font-medium hover:underline">
                  {tx("Passez à un plan supérieur", "Upgrade your plan", "Mejora tu plan")}
                </Link>{" "}
                {tx("pour continuer à analyser.", "to continue analyzing.", "para seguir analizando.")}
              </AlertDescription>
            </Alert>
          )}

          <Tabs value={activeInputTab} onValueChange={handleInputTabChange} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6">
              <TabsTrigger value="urls" className="flex items-center gap-2">
                <List className="w-4 h-4" />
                URLs
              </TabsTrigger>
              <TabsTrigger value="crawl" className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {tx("Crawler un site", "Crawl site", "Rastrear un sitio")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="urls">
              <BulkUrlInput 
                onScan={handleScan} 
                isLoading={isLoading}
                bulkLimit={isAuthenticated ? bulkLimit : 1}
                creditsAvailable={isAuthenticated ? creditsAvailable : 1}
              />
            </TabsContent>

            <TabsContent value="crawl">
              <Suspense fallback={null}>
              {isAuthenticated ? (
                <CrawlInput
                  onAnalyze={handleScan}
                  isAnalyzing={isLoading}
                  creditsAvailable={creditsAvailable}
                  bulkLimit={bulkLimit}
                  maxPages={currentPlan?.crawlLimit || 50}
                />
              ) : (
                <Alert className="max-w-3xl mx-auto border-primary/20 bg-primary/5">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-primary">
                    {tx(
                      "Créez un compte gratuit pour utiliser le crawler de site.",
                      "Create a free account to use the site crawler.",
                      "Crea una cuenta gratuita para usar el rastreador de sitios."
                    )}
                  </AlertDescription>
                </Alert>
              )}
              </Suspense>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-12 space-y-4">
          {/* Restored from history indicator */}
          {isRestored && !hasNewScan && results.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <History className="w-4 h-4" />
                {tx(
                  "Vos dernières analyses sont affichées ci-dessous.",
                  "Your latest analyses are shown below.",
                  "Tus últimos análisis se muestran a continuación."
                )}
              </div>
              <Link to="/account?tab=history" className="text-xs text-primary hover:underline">
                {tx("Voir tout l'historique", "View full history", "Ver todo el historial")}
              </Link>
            </div>
          )}

          {results.length === 0 && !isLoading && <EmptyState />}

          {results.map((result, resultIndex) => (
            <Collapsible key={resultIndex} defaultOpen={true}>
              <div className="border border-border rounded-xl overflow-hidden bg-card">
                <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors group">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center">
                      {resultIndex + 1}
                    </span>
                    <div className="text-left min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {result.title || result.url}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {result.generatedSchemas.length} schema{result.generatedSchemas.length > 1 ? 's' : ''} • {result.pageType}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-2 space-y-6 border-t border-border">
                   <Suspense fallback={null}>
                    <PageAnalysis
                      url={result.url}
                      pageType={result.pageType}
                      title={result.title}
                      existingSchemas={result.existingSchemas}
                      suggestedSchemas={result.suggestedSchemas}
                      warnings={result.warnings}
                    />

                    <div className="space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {t.results.generatedSchemas} ({result.generatedSchemas.length})
                        </h3>
                        <ExportAllSchemas 
                          schemas={result.generatedSchemas} 
                          pageTitle={result.title}
                        />
                      </div>
                      {result.generatedSchemas.map((schema, index) => (
                        <SchemaCard
                          key={index}
                          title={schema.title}
                          type={schema.type}
                          jsonLd={schema.jsonLd}
                          isNew={schema.isNew}
                          isImproved={schema.isImproved}
                          originalSchema={schema.originalSchema}
                        />
                      ))}
                    </div>
                    </Suspense>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* Upsell after free trial used - placed after results */}
        {showFreeTrialUsedUpsell && results.length > 0 && (
          <Card className="mt-8 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {tx("Vous avez aimé votre analyse ?", "Enjoyed your analysis?", "¿Te gustó tu análisis?")}
                  </CardTitle>
                  <CardDescription>
                    {tx(
                      "Continuez sans engagement avec nos packs de crédits",
                      "Continue without commitment with our credit packs",
                      "Continúa sin compromiso con nuestros packs de créditos"
                    )}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    <Package className="w-3 h-3 mr-1" />
                    50 {tx("crédits", "credits", "créditos")} — 5€
                  </Badge>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    100 {tx("crédits", "credits", "créditos")} — 9€
                  </Badge>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    250 {tx("crédits", "credits", "créditos")} — 20€
                  </Badge>
                </div>
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/auth">
                      {tx("Créer un compte", "Create account", "Crear cuenta")}
                    </Link>
                  </Button>
                  <Button size="sm" asChild>
                    <Link to="/pricing" className="flex items-center gap-1">
                      {tx("Voir les offres", "See offers", "Ver ofertas")}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Below-fold sections - lazy loaded */}
        <Suspense fallback={null}>
          <SEOBenefits />
          <div className="mt-16">
            <MonitoringHighlight />
          </div>
          <GeneratorLinks />
          <FAQ />
        </Suspense>

      </div>

      <QuotaReachedDialog open={showQuotaDialog} onOpenChange={setShowQuotaDialog} />
    </MainLayout>
  );
};

export default Index;
